import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { useState } from 'react'
import { api, type CallbackAttemptRow } from '../api/client'

export function CallbackDetailPage() {
  const { callbackId = '' } = useParams<{ callbackId: string }>()
  const qc = useQueryClient()
  const [resendMsg, setResendMsg] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['callback', callbackId],
    queryFn: () => api.callbacks.detail(callbackId),
    enabled: callbackId.length > 0,
  })

  const resend = useMutation({
    mutationFn: () => api.callbacks.resend(callbackId),
    onSuccess: (res) => {
      setResendMsg(
        `Requeued. status=${res.status}, attempts=${res.attemptCount}, resends=${res.resendCount}.`,
      )
      void qc.invalidateQueries({ queryKey: ['callback', callbackId] })
    },
    onError: (err) => setResendMsg(`Failed: ${(err as Error).message}`),
  })

  if (isLoading) return <div>Loading…</div>
  if (error) return <div className="text-red-600">Failed: {(error as Error).message}</div>
  if (!data) return null

  const delivered = data.status === 'sent' && !!data.sentAt

  return (
    <div className="max-w-4xl">
      <div className="mb-4 flex items-center justify-between">
        <Link to="/callbacks" className="text-sm text-brand-700 hover:underline">← Callbacks</Link>
        <button
          onClick={() => {
            setResendMsg(null)
            resend.mutate()
          }}
          disabled={resend.isPending}
          className="rounded bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
        >
          {resend.isPending ? 'Resending…' : 'Resend callback'}
        </button>
      </div>
      {resendMsg && (
        <div className="mb-3 rounded border border-slate-200 bg-slate-50 p-2 text-xs">{resendMsg}</div>
      )}
      <h1 className="mb-4 text-xl font-bold">Callback {data.id.slice(0, 8)}</h1>

      <section className="mb-4 grid grid-cols-2 gap-3 rounded border border-slate-200 bg-white p-4 text-sm">
        <KV label="Event"        value={data.eventName ?? '—'} mono />
        <KV label="Status"       value={delivered ? 'delivered' : data.status} />
        <KV label="Integrator"   value={data.integratorName ?? data.integratorId} />
        <KV label="Callback URL" value={data.callbackUrl ?? '—'} mono />
        <KV label="Job ID"       value={data.jobId} mono />
        <KV label="Invoice"      value={
          data.invoiceId ? (
            <Link to={`/invoices/${data.invoiceId}`} className="font-mono text-brand-700 hover:underline">
              {data.invoiceNumber ?? data.invoiceId.slice(0, 8)}
            </Link>
          ) : '—'
        } />
        <KV label="Attempts"     value={String(data.attemptCount)} />
        <KV label="Admin resends" value={`${data.resendCount}${data.lastResentAt ? ' (last: ' + new Date(data.lastResentAt).toLocaleString() + ')' : ''}`} />
        <KV label="Created at"   value={new Date(data.createdAt).toLocaleString()} />
        <KV label="Sent at"      value={data.sentAt ? new Date(data.sentAt).toLocaleString() : '—'} />
        <KV label="Next retry"   value={data.nextRetryAt ? new Date(data.nextRetryAt).toLocaleString() : '—'} />
      </section>

      {data.lastError && (
        <section className="mb-4">
          <h2 className="mb-2 text-sm font-semibold uppercase text-slate-600">Last error</h2>
          <pre className="overflow-x-auto rounded border border-red-200 bg-red-50 p-3 text-xs text-red-800">{data.lastError}</pre>
        </section>
      )}

      <section className="mb-4">
        <h2 className="mb-2 text-sm font-semibold uppercase text-slate-600">
          Delivery attempts ({data.attempts.length})
        </h2>
        {data.attempts.length === 0 ? (
          <div className="text-xs text-slate-500">No attempts recorded yet.</div>
        ) : (
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-xs">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="p-2">#</th>
                  <th className="p-2">Started</th>
                  <th className="p-2">Duration</th>
                  <th className="p-2">HTTP</th>
                  <th className="p-2">Outcome</th>
                  <th className="p-2">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.attempts.map((a) => (
                  <AttemptRow key={a.id} a={a} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase text-slate-600">Payload</h2>
        <pre className="overflow-x-auto rounded border border-slate-200 bg-slate-50 p-3 text-xs">{JSON.stringify(data.payload, null, 2)}</pre>
      </section>
    </div>
  )
}

function AttemptRow({ a }: { a: CallbackAttemptRow }) {
  const [open, setOpen] = useState(false)
  const details = a.errorMessage || a.responseBody
  return (
    <>
      <tr className={a.outcome === 'sent' ? 'bg-white' : 'bg-red-50/30'}>
        <td className="p-2 font-mono">{a.attemptNumber}</td>
        <td className="p-2">{new Date(a.startedAt).toLocaleString()}</td>
        <td className="p-2">{a.durationMs} ms</td>
        <td className="p-2 font-mono">{a.httpStatus ?? '—'}</td>
        <td className={`p-2 font-semibold ${a.outcome === 'sent' ? 'text-emerald-700' : 'text-red-700'}`}>
          {a.outcome}
        </td>
        <td className="p-2">
          {details ? (
            <button
              onClick={() => setOpen((v) => !v)}
              className="text-brand-700 hover:underline"
            >
              {open ? 'hide' : 'show'}
            </button>
          ) : '—'}
        </td>
      </tr>
      {open && details && (
        <tr>
          <td colSpan={6} className="bg-slate-50 p-2">
            {a.errorMessage && (
              <div className="mb-2">
                <div className="text-[10px] font-semibold uppercase text-slate-500">Error</div>
                <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-red-800">{a.errorMessage}</pre>
              </div>
            )}
            {a.responseBody && (
              <div>
                <div className="text-[10px] font-semibold uppercase text-slate-500">Response body</div>
                <pre className="overflow-x-auto whitespace-pre-wrap text-xs">{a.responseBody}</pre>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

function KV({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={mono ? 'font-mono text-xs' : ''}>{value}</div>
    </div>
  )
}
